import { api } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export interface RecipeShoppingSelection {
    recipeId: string;
    servings?: number; // absolute desired servings; backend scales the recipe accordingly
}

export interface GenerateFromRecipesPayload {
    recipes: RecipeShoppingSelection[];
    name?: string; // name for a new list (ignored when shoppingListId is set)
    shoppingListId?: string; // merge into this existing list instead of creating one
}

export interface ShoppingListSummary {
    id: string;
    name: string;
    isCompleted: boolean;
    _count?: { items: number };
}

// Generate (or extend) a shopping list directly from recipes — no program needed.
export const generateShoppingListFromRecipes = async (payload: GenerateFromRecipesPayload) => {
    const response = await api.post('/shopping-lists/from-recipes', payload);
    return response.data;
};

export const useGenerateShoppingListFromRecipes = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: generateShoppingListFromRecipes,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });
            // Adding recipes to an existing list changes that list's own page cache.
            queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
        },
    });
};

// Lightweight list of the user's shopping lists, used to populate the "add to
// existing list" picker. Only fetches incomplete lists by default.
export const getShoppingListSummaries = async (): Promise<ShoppingListSummary[]> => {
    const response = await api.get('/shopping-lists', {
        params: { completed: false, limit: 100, sortBy: 'created', sortOrder: 'desc' },
    });
    return response.data.shoppingLists ?? [];
};

export const useShoppingListSummaries = (enabled = true) => {
    return useQuery({
        queryKey: ['shopping-lists', 'summaries'],
        queryFn: getShoppingListSummaries,
        enabled,
        staleTime: 30 * 1000,
    });
};

// Every list's name, finished ones included, so a suggested name for a new
// list can avoid all of them (#376).
export const useShoppingListNames = (enabled = true) => {
    return useQuery({
        queryKey: ['shopping-lists', 'names'],
        queryFn: async (): Promise<string[]> => {
            const response = await api.get('/shopping-lists', {
                params: { limit: 500, sortBy: 'created', sortOrder: 'desc' },
            });
            return ((response.data.shoppingLists ?? []) as { name: string }[]).map(
                (list) => list.name,
            );
        },
        enabled,
        staleTime: 30 * 1000,
    });
};

// ---------------------------------------------------------------------------
// One list and its items (#330, Phase A).
//
// Free-text rows ("Toilet paper") have `ingredient: null` and may have
// `amount: null`, so render `displayName` and never `ingredient.name`.
// ---------------------------------------------------------------------------

export type ShoppingItemSource = 'MENU' | 'RECIPE' | 'MANUAL';

/** The section the backend files every free-text row under. */
export const ADDED_BY_YOU_SECTION = 'Added by you';

export interface ShoppingListItem {
    id: string;
    ingredientId: string | null;
    name: string | null;
    displayName: string;
    amount: number | null;
    unit: string;
    section?: string | null;
    aisle?: string | null;
    source: ShoppingItemSource;
    userEdited: boolean;
    isPurchased: boolean;
    ingredient?: { id: string; name: string } | null;
}

export interface ShoppingListDetail {
    id: string;
    name: string;
    isCompleted: boolean;
    createdAt: string;
    completedAt?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    items: ShoppingListItem[];
}

/** A selected meal that added nothing to the list (no recipe, so nothing to buy). */
export interface UnlistedMeal {
    date: string;
    mealType?: string;
    name: string;
}

/**
 * Navigation state the list page understands. The "Shop for your plan" picker
 * passes the generate response's `unlistedMeals` (and `warnings`) here when it
 * navigates to `/shopping-lists/:id`.
 */
export interface ShoppingListRouteState {
    unlistedMeals?: UnlistedMeal[];
    warnings?: { code: string; message: string }[];
}

export type AddShoppingItemPayload =
    | { name: string; amount?: number | null; unit?: string }
    | { ingredientId: string; amount: number; unit: string };

export interface UpdateShoppingItemPayload {
    amount?: number | null;
    unit?: string;
    name?: string;
    isPurchased?: boolean;
}

export const shoppingListKey = (listId: string) => ['shopping-list', listId] as const;

export const getShoppingList = async (listId: string): Promise<ShoppingListDetail> => {
    const response = await api.get(`/shopping-lists/${listId}`);
    return response.data;
};

export const addShoppingListItem = async (
    listId: string,
    payload: AddShoppingItemPayload,
): Promise<ShoppingListItem> => {
    const response = await api.post(`/shopping-lists/${listId}/items`, payload);
    return response.data;
};

export const updateShoppingListItem = async (
    listId: string,
    itemId: string,
    payload: UpdateShoppingItemPayload,
): Promise<ShoppingListItem> => {
    const response = await api.put(`/shopping-lists/${listId}/items/${itemId}`, payload);
    return response.data;
};

export const deleteShoppingListItem = async (listId: string, itemId: string): Promise<void> => {
    await api.delete(`/shopping-lists/${listId}/items/${itemId}`);
};

export const useShoppingList = (listId: string | undefined) => {
    return useQuery({
        queryKey: shoppingListKey(listId ?? ''),
        queryFn: () => getShoppingList(listId!),
        enabled: !!listId,
    });
};

/** The list overview and the "add to existing list" picker read these. */
const invalidateOverviews = (queryClient: ReturnType<typeof useQueryClient>) =>
    queryClient.invalidateQueries({ queryKey: ['shopping-lists'] });

const replaceItem = (
    list: ShoppingListDetail | undefined,
    itemId: string,
    change: (item: ShoppingListItem) => ShoppingListItem,
) =>
    list
        ? { ...list, items: list.items.map((item) => (item.id === itemId ? change(item) : item)) }
        : list;

/**
 * Tick or untick a row. The cache changes straight away and goes back if the
 * server refuses, so a tick never waits on the network (the shop may have poor
 * signal) and a later offline queue can slot in here.
 */
export const useTickShoppingItem = (listId: string) => {
    const queryClient = useQueryClient();
    const key = shoppingListKey(listId);
    return useMutation({
        mutationFn: ({ itemId, isPurchased }: { itemId: string; isPurchased: boolean }) =>
            updateShoppingListItem(listId, itemId, { isPurchased }),
        onMutate: async ({ itemId, isPurchased }) => {
            await queryClient.cancelQueries({ queryKey: key });
            const previous = queryClient
                .getQueryData<ShoppingListDetail>(key)
                ?.items.find((item) => item.id === itemId);
            queryClient.setQueryData<ShoppingListDetail>(key, (list) =>
                replaceItem(list, itemId, (item) => ({ ...item, isPurchased })),
            );
            return { previous };
        },
        onError: (_error, { itemId }, context) => {
            // Put back only this row, so a failed tick does not undo other
            // ticks made while it was in flight.
            const before = context?.previous;
            if (before) {
                queryClient.setQueryData<ShoppingListDetail>(key, (list) =>
                    replaceItem(list, itemId, (item) => ({
                        ...item,
                        isPurchased: before.isPurchased,
                    })),
                );
            }
        },
        onSuccess: () => invalidateOverviews(queryClient),
    });
};

export const useAddShoppingItem = (listId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: AddShoppingItemPayload) => addShoppingListItem(listId, payload),
        onSuccess: (saved) => {
            // A catalogue add can top up an existing row (same id comes back),
            // so replace that row or append a new one. Writing the answer into
            // the cache, rather than refetching, keeps any tick still in flight.
            queryClient.setQueryData<ShoppingListDetail>(shoppingListKey(listId), (list) => {
                if (!list) return list;
                const exists = list.items.some((item) => item.id === saved.id);
                return {
                    ...list,
                    items: exists
                        ? list.items.map((item) =>
                              item.id === saved.id ? { ...item, ...saved } : item,
                          )
                        : [...list.items, saved],
                };
            });
            invalidateOverviews(queryClient);
        },
    });
};

export const useUpdateShoppingItem = (listId: string) => {
    const queryClient = useQueryClient();
    const key = shoppingListKey(listId);
    return useMutation({
        mutationFn: ({ itemId, ...payload }: UpdateShoppingItemPayload & { itemId: string }) =>
            updateShoppingListItem(listId, itemId, payload),
        onSuccess: (saved, { itemId }) => {
            queryClient.setQueryData<ShoppingListDetail>(key, (list) =>
                replaceItem(list, itemId, (item) => ({ ...item, ...saved })),
            );
            invalidateOverviews(queryClient);
        },
    });
};

/**
 * Rename a list (#376). Only the name is taken from the answer: PUT returns
 * the items without the displayName the page renders.
 */
export const useRenameShoppingList = (listId: string) => {
    const queryClient = useQueryClient();
    const key = shoppingListKey(listId);
    return useMutation({
        mutationFn: async (name: string): Promise<{ name: string }> => {
            const response = await api.put(`/shopping-lists/${listId}`, { name });
            return { name: response.data?.name ?? name };
        },
        onSuccess: ({ name }) => {
            queryClient.setQueryData<ShoppingListDetail>(key, (list) =>
                list ? { ...list, name } : list,
            );
            invalidateOverviews(queryClient);
        },
    });
};

export const useDeleteShoppingItem = (listId: string) => {
    const queryClient = useQueryClient();
    const key = shoppingListKey(listId);
    return useMutation({
        mutationFn: (itemId: string) => deleteShoppingListItem(listId, itemId),
        onSuccess: (_data, itemId) => {
            queryClient.setQueryData<ShoppingListDetail>(key, (list) =>
                list ? { ...list, items: list.items.filter((item) => item.id !== itemId) } : list,
            );
            invalidateOverviews(queryClient);
        },
    });
};

/**
 * The client's plan dates, to label a list "Days 1 to 7" and to offer
 * "+ Add days" while the plan is upcoming or running. Kept to the fields the
 * list page needs, under its own query key.
 */
export interface PlanDates {
    startDate: string;
    endDate: string;
    phase?: 'UPCOMING' | 'IN_PROGRESS' | 'FINISHED';
}

export const usePlanDatesForShopping = (today: string, enabled = true) => {
    return useQuery({
        queryKey: ['shopping-plan-dates', today],
        queryFn: async (): Promise<PlanDates | null> => {
            const response = await api.get('/programs/my-plan', { params: { today } });
            const plan = response.data?.plan;
            return plan
                ? { startDate: plan.startDate, endDate: plan.endDate, phase: plan.phase }
                : null;
        },
        enabled,
        staleTime: 5 * 60 * 1000,
        retry: false,
    });
};
